import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Edit, ExternalLink } from 'lucide-react';

interface Backlink {
  id: string;
  website_name: string;
  website_url: string;
  domain_authority: number | null;
  category: string;
  contact_email: string | null;
  contact_name: string | null;
  outreach_status: string;
  outreach_date: string | null;
  response_date: string | null;
  backlink_url: string | null;
  backlink_type: string | null;
  notes: string | null;
  created_at: string;
}

interface BacklinksTableProps {
  backlinks: Backlink[];
  onUpdate: () => void;
}

export const BacklinksTable = ({ backlinks, onUpdate }: BacklinksTableProps) => {
  const [editingBacklink, setEditingBacklink] = useState<Backlink | null>(null);
  const [formData, setFormData] = useState<Partial<Backlink>>({});

  const handleEdit = (backlink: Backlink) => {
    setEditingBacklink(backlink);
    setFormData(backlink);
  };

  const handleUpdate = async () => {
    if (!editingBacklink) return;

    const { error } = await supabase
      .from('backlinks')
      .update({
        website_name: formData.website_name,
        website_url: formData.website_url,
        domain_authority: formData.domain_authority,
        category: formData.category,
        contact_email: formData.contact_email,
        contact_name: formData.contact_name,
        outreach_status: formData.outreach_status,
        outreach_date: formData.outreach_date,
        response_date: formData.response_date,
        backlink_url: formData.backlink_url,
        backlink_type: formData.backlink_type,
        notes: formData.notes,
      })
      .eq('id', editingBacklink.id);

    if (error) {
      toast.error('Failed to update backlink');
    } else {
      toast.success('Backlink updated successfully');
      setEditingBacklink(null);
      onUpdate();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <Badge className="bg-blue-600">Published</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-600">Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive">Invalid Link</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getOutreachBadge = (status: string) => {
    switch (status) {
      case 'not_contacted':
        return <Badge variant="outline">Not Contacted</Badge>;
      case 'pending_outreach':
        return <Badge className="bg-orange-600">Pending Outreach</Badge>;
      case 'email_sent':
        return <Badge className="bg-purple-600">Email Sent</Badge>;
      case 'awaiting_reply':
        return <Badge className="bg-yellow-600">Awaiting Reply</Badge>;
      case 'follow_up_1':
        return <Badge className="bg-blue-600">Follow-up 1 Done</Badge>;
      case 'follow_up_2':
        return <Badge className="bg-indigo-600">Follow-up 2 Done</Badge>;
      case 'connected':
        return <Badge className="bg-green-600">Connected</Badge>;
      case 'invalid_link':
        return <Badge variant="destructive">Invalid Link</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (backlinks.length === 0) {
    return (
      <p className="text-center py-8 text-muted-foreground">
        No backlinks tracked yet. Add your first backlink outreach!
      </p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Website</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>DA</TableHead>
              <TableHead>Link Status</TableHead>
              <TableHead>Outreach Status</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {backlinks.map((backlink) => (
              <TableRow key={backlink.id}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{backlink.website_name}</span>
                    <a
                      href={backlink.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge>{backlink.category}</Badge>
                  {(backlink as any).company_type && (
                    <Badge variant="outline" className="ml-1">{(backlink as any).company_type}</Badge>
                  )}
                </TableCell>
                <TableCell>{backlink.domain_authority || 'N/A'}</TableCell>
                <TableCell>{getStatusBadge((backlink as any).processing_status || 'pending')}</TableCell>
                <TableCell>{getOutreachBadge(backlink.outreach_status)}</TableCell>
                <TableCell className="text-sm">
                  {backlink.contact_name && <div>{backlink.contact_name}</div>}
                  {backlink.contact_email && (
                    <div className="text-muted-foreground">{backlink.contact_email}</div>
                  )}
                  {(backlink as any).reply_received && (
                    <Badge className="bg-green-600 mt-1">Reply Received</Badge>
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(backlink)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!editingBacklink} onOpenChange={() => setEditingBacklink(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Backlink</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="website_name">Website Name</Label>
                <Input
                  id="website_name"
                  value={formData.website_name || ''}
                  onChange={(e) => setFormData({ ...formData, website_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="website_url">Website URL</Label>
                <Input
                  id="website_url"
                  value={formData.website_url || ''}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  value={formData.category || ''}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="domain_authority">Domain Authority</Label>
                <Input
                  id="domain_authority"
                  type="number"
                  value={formData.domain_authority || ''}
                  onChange={(e) => setFormData({ ...formData, domain_authority: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name || ''}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="contact_email">Contact Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email || ''}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="outreach_status">Outreach Status</Label>
              <Select
                value={formData.outreach_status}
                onValueChange={(value) => setFormData({ ...formData, outreach_status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="not_contacted">Not Contacted</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="secured">Secured</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="backlink_type">Backlink Type</Label>
                <Input
                  id="backlink_type"
                  value={formData.backlink_type || ''}
                  onChange={(e) => setFormData({ ...formData, backlink_type: e.target.value })}
                  placeholder="Guest Post, Directory, etc."
                />
              </div>
              <div>
                <Label htmlFor="backlink_url">Backlink URL</Label>
                <Input
                  id="backlink_url"
                  value={formData.backlink_url || ''}
                  onChange={(e) => setFormData({ ...formData, backlink_url: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
              />
            </div>

            <Button onClick={handleUpdate} className="w-full">
              Update Backlink
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
